import styled, { keyframes } from "styled-components";
import { useState, useEffect } from "react";
import { theme, breakpoint } from "@utilities";
import { ISharedPaymentLink } from "@interfaces";
import { quoteApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

interface ViewSentEmailsModalProps {
    quoteId?: string;
    handleResend: (travelerId: number) => Promise<void>;
    onClose: () => void;
}

// const formatDate = (dateString: string): string => {
//     const date = new Date(dateString);
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const day = String(date.getDate()).padStart(2, "0");
//     return `${year}/${month}/${day}`;
// };

export const ViewSentEmailsModal = ({
    quoteId,
    handleResend,
    onClose,
}: ViewSentEmailsModalProps) => {
    const [sentEmails, setSentEmails] = useState<ISharedPaymentLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [resendingId, setResendingId] = useState<number | null>(null);

    useEffect(() => {
        const fetchSentEmails = async () => {
            if (!quoteId) return;
            try {
                setLoading(true);
                const response = await quoteApi.getSharedPaymentLinks(quoteId);
                setSentEmails(response.data.data || []);
            } catch (error) {
                console.error("Failed to fetch sent emails:", error);
                ErrorToast({ Message: "Failed to load sent emails" });
            } finally {
                setLoading(false);
            }
        };

        fetchSentEmails();
    }, [quoteId]);

    return (
        <>
            <Overlay onClick={onClose} />
            <BottomSheet onClick={(e) => e.stopPropagation()}>
                <div className="details-container">
                    <div className="header">
                        <h2>Sent Links</h2>
                        <CloseButton onClick={onClose}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#080914"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </CloseButton>
                    </div>

                    <ScrollBox>
                        {loading ? (
                            <p style={{ color: "#6e7375", fontSize: "14px", lineHeight: "24px" }}>
                                Loading...
                            </p>
                        ) : sentEmails.length === 0 ? (
                            <p style={{ color: "#6e7375", fontSize: "14px", lineHeight: "24px" }}>
                                Payment link not shared yet.
                            </p>
                        ) : (
                            sentEmails.map((traveler) => (
                                <div className="details" key={traveler.id}>
                                    <div>
                                        <h1>{`${traveler.name}`}</h1>
                                        <p>{traveler.email}</p>
                                    </div>
                                    <div className="resend-section">
                                        <ResendButton
                                            disabled={resendingId === traveler.id}
                                            onClick={async () => {
                                                setResendingId(traveler.id);
                                                await handleResend(Number(traveler.id));
                                                setResendingId(null);
                                            }}
                                        >
                                            {resendingId === traveler.id
                                                ? "Resending..."
                                                : "Resend"}
                                        </ResendButton>
                                    </div>
                                </div>
                            ))
                        )}
                    </ScrollBox>
                </div>
            </BottomSheet>
        </>
    );
};

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    @media (max-width: 768px) {
        min-width: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    ${breakpoint.breakTablet`
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;

    @media (max-width: 768px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    ${breakpoint.breakTablet`
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 10;
    `}
`;

const BottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall, breakpoint.breakMobileMedium].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;

        .details-container{
            display: flex;
            flex-direction: column;
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px !important;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div{
                    display : flex;
                    flex-direction : column;
                    flex: 1;
                    h1{
                        font-size : 24px;
                        font-weight : 600;
                        margin-bottom: 6px;
                    }
                    p{
                        font-size : 12px;
                        color : #6E7375;
                        margin-bottom: 0;
                    }
                }
                .resend-section {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 4px;
                    flex: none;
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
        .styled-buttons {
            display : flex;
            gap : 14px;
        }
    `,
    )}

    @media (max-width: 768px) {
        display: block !important;
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        top: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;
        height: auto !important;
        min-height: auto !important;

        .details-container {
            h2 {
                margin-bottom: 20px;
            }
            .details {
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div h1 {
                    margin-bottom: 6px;
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
    }

    ${breakpoint.breakTablet`
        display: block;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 700px;
        max-width: 90vw;
        z-index: 15;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        padding: 50px 30px;
        animation: none;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        
        .details-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-bottom: 30px;
            h2 {
                font-size: 26px;
                font-weight: 700;
                margin-bottom: 20px;
            }
            .details {
                display: flex;
                align-items: center;
                gap: 16px;
                div {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    flex: 1;
                    h1 {
                        font-size: 24px;
                        font-weight: 600;
                    }
                    p {
                        font-size: 12px;
                        color: #6e7375;
                    }
                }
                .resend-section {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 4px;
                    flex: none;
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
        
        .styled-buttons {
            display: flex;
            gap: 24px;
        }
    `}
`;

const ScrollBox = styled.div`
    max-height: 20vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }

    @media (max-width: 768px) {
        gap: 0;
        -webkit-overflow-scrolling: touch;
        -webkit-scrollbar-width: thin;
        -webkit-scrollbar-color: #d1d5db transparent;

        .details {
            margin-bottom: 20px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const ResendButton = styled.button`
    min-width: 100px;
    height: 40px;
    font-weight: 600;
    font-size: 14px;
    border: none;
    background: transparent;
    color: ${theme.colors.highlight};
    border-radius: 6px;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media (max-width: 768px) {
        min-height: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }
`;

// const LastSentDate = styled.p`
//     font-size: 10px;
//     color: #9ca3af;
//     margin: 0;
//     text-align: right;
//     font-family: 'Plus Jakarta Sans';
//     font-weight: 400;
// `;
